import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LookupResponse } from '@core/models/common.model';
import { ScaleSliderComponent } from '@shared/components/scale-slider/scale-slider.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { exclusiveSelection } from '@shared/utils/exclusive-selection';
import { createCollectionGroup } from '../../cia-fixed-rows';
import { CiaFormState, FunctionalRiskGroup } from '../../cia-form-groups';
import { checklistItems, exclusiveProblemId, notApplicableProblems, screeningLabel, underlyingProblemGroups } from '../../cia-lookups';
import { CiaStepBase } from '../../cia-step-base';
import { CiaChecklistComponent } from '../cia-checklist/cia-checklist.component';

// Step 3, Functional impact and risk: the underlying problems (any number, or "Not applicable"), what the problem does to the member's work and to
// them, the screenings that were done, and the overall risk rating.
@Component({
  selector: 'cia-step-functional',
  imports: [ReactiveFormsModule, CiaChecklistComponent, ScaleSliderComponent, ...SharedMaterialUIModule],
  templateUrl: './cia-step-functional.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CiaStepFunctionalComponent extends CiaStepBase {
  private readonly formBuilder = inject(FormBuilder);

  // The step's form group, held by the form so its validity counts there.
  readonly group = input.required<FunctionalRiskGroup>();
  // The last selection of the underlying problems, for the stand-alone "Not applicable" choice.
  readonly state = input.required<CiaFormState>();
  // The Suicide Risk Screener (MW19) completed for this case, if any.
  readonly riskScreener = input<{ screenedDate: string; riskRatingName: string } | null>(null);

  // The work performance rows (one for each kind of impact), as an untyped array for the template.
  protected workRows(): FormArray {
    return this.group().controls.workPerformanceImpactRatings as FormArray;
  }

  // Makes the row of a screening when it is ticked.
  protected readonly createScreening = (typeId: string): FormGroup =>
    createCollectionGroup(this.formBuilder, 'riskScreenings', { riskScreeningTypeId: typeId });

  // The screenings that can be ticked, in the order of the review form.
  protected screenings(): LookupResponse[] {
    return checklistItems(this.lookups(), 'riskScreeningTypes');
  }

  // A screening is named for what it looks for and the tool that does it.
  protected readonly screeningLabel = screeningLabel;

  protected screeningRows(): FormArray {
    return this.group().controls.riskScreenings as FormArray;
  }

  // The underlying problems offered under their cluster, and the stand-alone "Not applicable".
  protected underlyingProblemGroups(): { name: string; problems: LookupResponse[] }[] {
    return underlyingProblemGroups(this.lookups());
  }

  protected notApplicableProblems(): LookupResponse[] {
    return notApplicableProblems(this.lookups());
  }

  // "Not applicable" cannot be combined with the others: choosing it clears them, and choosing another clears it.
  protected underlyingProblemsChanged(selected: string[]): void {
    const control = this.group().controls.underlyingProblemIds;
    const state = this.state();
    const result = exclusiveSelection(selected, state.previousUnderlyingProblems, exclusiveProblemId(this.lookups()));

    if (result !== selected) {
      control.setValue(result);
    }

    state.previousUnderlyingProblems = result;
  }
}
